import React, { useState, useEffect } from "react";
import { useMoney } from "../../hooks/useMoney";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import { updateDistributor, updateDistributorProgramPaymentGateway } from "../../features/distributor/DistributorSlice";
import { RootState } from "../../store/store";
import { Loader2, AlertCircle, ArrowLeft, CheckCircle } from "lucide-react";
import { ProgramForm } from "./BuildCataloguePage";
import { addProgramDistributor, clearError } from "../../features/distributor/DistributorSlice";

/* --------------------------------- Interfaces ------------------------------ */

interface EditReviewPageProps {
  currency: string;
  country: string;
  paymentType: number;
  name: string;
  businessName: string;
  hostname: string;
  programIds: ProgramForm;
  whiteListedIps: string[];
  isNonUniqueReferenceIdAllowed: boolean;
  paymentGatewayId: number | null;
  paymentGatewayConfig: Record<string, any>;
  logo?: File | string | null;
  contactInformation: {
    email: string;
    phone: string;
    contactPerson: string;
    address: {
      street: string;
      city: string;
      state: string;
      country: string;
      zipCode: string;
    };
  };
  onBack: () => void;
  onBackListing: () => void;
  distributorId: number;
  color?: string;
  isPurchaseOrderRequired: boolean;
  allowPurchaseOrderCreation: boolean;
}

const EditReviewPage: React.FC<EditReviewPageProps> = ({
  onBackListing,
  onBack,
  distributorId,
  programIds,
  name,
  businessName,
  hostname,
  country,
  currency,
  contactInformation,
  paymentType,
  whiteListedIps,
  isNonUniqueReferenceIdAllowed,
  paymentGatewayId,
  paymentGatewayConfig,
  logo,
  color,
  isPurchaseOrderRequired,
  allowPurchaseOrderCreation
}) => {
  const { symbol } = useMoney();
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector((state: RootState) => state.distributor);
  const { paymentGateways } = useAppSelector((state: RootState) => state.paymentGateway);

  /* --------------------------------- States ------------------------------ */

  const [success, setSuccess] = useState(false);

  /* --------------------------------- Hooks ------------------------------ */

  useEffect(() => {
    if (success) {
      onBackListing();
    }
  }, [success, onBackListing]);


  /* ------------------------------ Event Handlers ---------------------------- */

  const handleUpdateDistributor = async () => {
    const formData = new FormData();
    
    // Append fields exactly as in the curl example
    formData.append('name', name);
    formData.append('businessName', businessName);
    formData.append('hostname', hostname);
    formData.append('country', country);
    formData.append('currency', currency);
    formData.append('paymentType', String(paymentType));
    formData.append('isNonUniqueReferenceIdAllowed', String(isNonUniqueReferenceIdAllowed));
    formData.append('contactInformation', JSON.stringify(contactInformation));
    formData.append('whiteListedIps', JSON.stringify(whiteListedIps));
    formData.append('paymentGatewayId', String(paymentGatewayId ?? 0));
    formData.append('isPurchaseOrderRequired', String(isPurchaseOrderRequired));
    formData.append('allowPurchaseOrderCreation', String(allowPurchaseOrderCreation));
    if (logo instanceof File) formData.append('logo', logo);
    formData.append('theme', color || '#22C55E');

    const response = await dispatch(
      updateDistributor({
        distributorId,
        data: formData,
      })
    );

    if (response.type === "distributor/updateDistributor/fulfilled") {
      const response2 = await dispatch(
        addProgramDistributor({ distributorId: distributorId.toString(), program: programIds })
      );

      if (response2.type === "distributor/addProgramDistributor/fulfilled") {
        // Build program-payment gateway combinations if a payment gateway is selected
        if (paymentGatewayId) {
          const programPaymentGateways = Object.entries(programIds)
            .filter(([, value]) => value.checked && value.enablePaymentGateway)
            .map(([programId]) => ({
              programId: Number(programId),
              paymentGatewayId: paymentGatewayId,
            }));

          const response3 = await dispatch(
            updateDistributorProgramPaymentGateway({
              distributorId,
              programPaymentGateways,
            })
          );

          if (response3.type === "distributor/updateDistributorProgramPaymentGateway/rejected") {
            return;
          }
        }

        setSuccess(true);
      }
    }
  };

  // Find the selected payment gateway
  const selectedGateway = paymentGateways?.find(gateway => gateway.paymentGatewayId === paymentGatewayId);



  return (
    <div className="bg-white h-full">
      {/* Header */}
      <div className="flex border-b border-cardborder mb-8 flex-col md:flex-row md:items-center md:justify-between max-w-7xl mx-auto px-7 py-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              dispatch(clearError());
              onBack();
            }}
            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Review
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Distributor <strong>{name}</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8">
        {/* Error State */}
        {error && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex items-center gap-2 justify-center text-red-500">
              <AlertCircle className="w-5 h-5" />
              <span>{error ? error : "Something went wrong!"}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-6 flex flex-col justify-center min-h-[120px] transition-shadow duration-200 transition-colors border-[1px] border-cardborder mb-8">
            <div className="flex justify-center items-center gap-2">
              <Loader2 className="w-10 h-10 animate-spin text-brand/80" />
              <span className="text-sm text-gray-500">Updating distributor...</span>
            </div>
          </div>
        )}

        {/* Review Content */}
        {!loading && !error && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Left Section - Distributor Details */}
            <div className="bg-white rounded-xl p-6 border-[1px] border-cardborder">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">Distributor Details</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Account Name</span>
                  <span className="text-sm text-gray-900">{name}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Legal Name</span>
                  <span className="text-sm text-gray-900">{businessName}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Country</span>
                  <span className="text-sm text-gray-900">{country}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Currency</span>
                  <span className="text-sm text-gray-900">{currency}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Payment Terms</span>
                  <span className="text-sm text-gray-900">{paymentType === 0 ? "Prepaid" : "Postpaid"}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Contact Email</span>
                  <span className="text-sm text-gray-900">{contactInformation.email}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Contact Phone</span>
                  <span className="text-sm text-gray-900">{contactInformation.phone}</span>
                </div>
                
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Contact Person</span>
                  <span className="text-sm text-gray-900">{contactInformation.contactPerson}</span>
                </div>
                
                <div className="py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600 block mb-3">Address</span>
                  <div className="text-sm text-gray-900 space-y-1">
                    <div>{contactInformation.address.street}</div>
                    <div>{contactInformation.address.city}, {contactInformation.address.state}</div>
                    <div>{contactInformation.address.country}, {contactInformation.address.zipCode}</div>
                  </div>
                </div>

                <div className="py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600 block mb-3">Whitelisted IPs</span>
                  <div className="flex flex-wrap gap-2">
                    {whiteListedIps.map((ip, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs">
                        {ip}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-sm font-medium text-gray-600">Non-Unique Reference IDs</span>
                  <span className="text-sm text-gray-900">{isNonUniqueReferenceIdAllowed ? "Allowed" : "Not Allowed"}</span>
                </div>

                {paymentGatewayId && selectedGateway && (
                  <div className="py-3">
                    <span className="text-sm font-medium text-gray-600 block mb-3">Payment Gateway</span>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-sm font-medium text-gray-900 mb-2">{selectedGateway.name}</div>
                      <div className="space-y-2">
                        {Object.entries(paymentGatewayConfig).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-center text-xs">
                            <span className="text-gray-600">{key}:</span>
                            <span className="text-gray-900 bg-white px-2 py-1 rounded border">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Section - Available Programs */}
            <div className="bg-white rounded-xl p-6 border-[1px] border-cardborder">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Available Programs <span className="text-brand">({Object.values(programIds).filter(p => p.checked).length})</span>
              </h2>
              
              <div className="space-y-4">
                {Object.entries(programIds).filter(([, value]) => value.checked).map(([programId, value]) => (
                  <div key={programId} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={value.program_details.logo}
                        alt={value.program_details.displayName}
                        className="w-16 h-10 object-contain rounded-lg p-1"
                      />
                      <h3 className="text-sm font-semibold text-gray-900">
                        {value.program_details.displayName}
                      </h3>
                    </div>
                    
                    <div className="space-y-2">
                      <span className="text-xs font-medium text-gray-600">Discount Ranges:</span>
                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        {value.discount_ranges.map((range, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-brand flex-shrink-0" />
                            <span className="flex-1">
                            {symbol}{range.min_order_value} to {range.max_order_value === null ? '∞' : `${symbol}${range.max_order_value}`}
                            </span>
                            <span className="text-brand font-semibold">
                              {range.discount}% 
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!loading && !error && (
          <div className="flex justify-between pb-8">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none transition-colors"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleUpdateDistributor}
              className="px-6 py-2 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none transition-colors"
            >
              Update Distributor
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditReviewPage;